/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { useStateRef } from 'src/luis/projects/analytics/hooks/useStateRef';

type DashboardCard = 'none' | 'overallPredictions' | 'predictionsPerIntent' | 'intentsWithErrors';

type CardsExpandingState = {
    [key in Exclude<DashboardCard, 'none'>]: boolean;
};

type DashboardCardStates = {
    lastActiveCard: DashboardCard;
    cardsExpandingState: CardsExpandingState;
};

/**
 * Keeps track of the UI state in the dashboard page for the
 * seek of retrieving this state anytime the user return back
 * to the page in the same session (without refreshing the page).
 *
 * This hook take care of the cards state.
 */
export const useCardsTracker = () => {
    const [dashboardCardStates, setDashboardCardStates, dashboardCardStatesRef] = useStateRef<DashboardCardStates>({
        lastActiveCard: 'none',
        cardsExpandingState: { overallPredictions: true, predictionsPerIntent: false, intentsWithErrors: false }
    });

    const sessionStorage = getStorage('sessionStorage');

    React.useEffect(() => {
        const previouslyStoredState = tryParseJson<DashboardCardStates>(sessionStorage.getData('analyticsCardsTracker'));

        if (previouslyStoredState) {
            setDashboardCardStates(previouslyStoredState);
            setTimeout(scrollToLastActiveCard, 100);
        }

        return () => sessionStorage.setData('analyticsCardsTracker', JSON.stringify(dashboardCardStatesRef.current));
    }, []);

    /**
     * Toggles the expanding state of the given card.
     */
    const toggleCard = (card: Exclude<DashboardCard, 'none'>) => {
        setDashboardCardStates({
            lastActiveCard: card,
            cardsExpandingState: {
                ...dashboardCardStatesRef.current.cardsExpandingState,
                [card]: !dashboardCardStatesRef.current.cardsExpandingState[card]
            }
        });
    };

    /**
     * Sets the last used card number.
     */
    const setLastActiveCard = (card: DashboardCard) => {
        setDashboardCardStates({ ...dashboardCardStatesRef.current, lastActiveCard: card });
    };

    /**
     * Apply the auto scrolling to the last previously used card.
     */
    const scrollToLastActiveCard = () => {
        const overallCard = document.querySelector('#overallCard');
        const intentsWithErrorsCard = document.querySelector('#intentsWithErrorsCard');
        const predictionsPerIntentCard = document.querySelector('#predictionsPerIntentCard');
        switch (dashboardCardStatesRef.current.lastActiveCard) {
            case 'overallPredictions':
                if (overallCard && dashboardCardStatesRef.current.cardsExpandingState.overallPredictions) {
                    overallCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
                break;
            case 'predictionsPerIntent':
                if (predictionsPerIntentCard && dashboardCardStatesRef.current.cardsExpandingState.predictionsPerIntent) {
                    predictionsPerIntentCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
                break;
            case 'intentsWithErrors':
                if (intentsWithErrorsCard && dashboardCardStatesRef.current.cardsExpandingState.intentsWithErrors) {
                    intentsWithErrorsCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
                break;
            default:
        }
    };

    return {
        toggleCard,
        setLastActiveCard,
        dashboardCardStates
    };
};
